/**
 * LineChart (Phase 3, §6.3/§6.4) — SPINE. One or more 2px lines on a single EUR
 * axis with ≥8px dots and optional direct end-labels. Used for the net-worth
 * single-series line (no legend — the title names it; axis may cross zero) and
 * the multi-line bucket-trend toggle. Handles `null` gaps by breaking the line.
 */
import { useState } from 'react';
import { money, moneyShort } from '../../../lib/format.ts';
import { Axis, niceTicks, scaleLinear, type AxisTick } from './Axis.tsx';
import { ChartFrame } from './ChartFrame.tsx';
import { Legend, type LegendItem } from './Legend.tsx';
import { Tooltip, type TooltipRow } from './Tooltip.tsx';

export interface LineSeries {
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
}

export interface LineChartProps {
  title: string;
  subtitle?: string;
  description?: string;
  categories: string[];
  series: LineSeries[];
  formatValue?: (n: number) => string;
  formatTick?: (n: number) => string;
  /** Show the legend (default true; pass false for a single named series). */
  showLegend?: boolean;
  /** Direct-label the last defined point of each series. */
  directLabelLast?: boolean;
  /** Force 0 into the domain. */
  includeZero?: boolean;
  aspect?: number;
}

const M = { left: 56, right: 20, top: 16, bottom: 28 };

export function LineChart({
  title,
  subtitle,
  description,
  categories,
  series,
  formatValue = money,
  formatTick = moneyShort,
  showLegend = true,
  directLabelLast = false,
  includeZero = false,
  aspect = 0.5,
}: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const n = categories.length;

  const legendItems: LegendItem[] = series.map((s) => ({
    label: s.label,
    color: s.color,
    shape: 'line' as const,
  }));

  const columns = ['Zeitraum', ...series.map((s) => s.label)];
  const rows: (string | number)[][] = categories.map((cat, i) => [
    cat,
    ...series.map((s) => {
      const v = s.values[i];
      return v === null || v === undefined ? '–' : formatValue(v);
    }),
  ]);

  const scrollMinWidth = n > 8 ? n * 56 + M.left + M.right : undefined;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      description={description}
      aspect={aspect}
      scrollMinWidth={scrollMinWidth}
      legend={showLegend && series.length >= 2 ? <Legend items={legendItems} /> : undefined}
      table={{ columns, rows, caption: title }}
    >
      {({ width, height }) => {
        const plotLeft = M.left;
        const plotRight = width - M.right;
        const plotTop = M.top;
        const plotBottom = height - M.bottom;
        const innerW = Math.max(0, plotRight - plotLeft);
        const innerH = Math.max(0, plotBottom - plotTop);

        const defined = series.flatMap((s) =>
          s.values.filter((v): v is number => v !== null && v !== undefined),
        );
        const pool = includeZero ? [0, ...defined] : defined;
        const dataMin = pool.length > 0 ? Math.min(...pool) : 0;
        const dataMax = pool.length > 0 ? Math.max(...pool) : 1;
        const scale = niceTicks(dataMin, dataMax, 5);
        const yScale = scaleLinear(scale.min, scale.max, plotBottom, plotTop);
        const showBaseline = scale.min < 0 && scale.max > 0;

        const yTicks: AxisTick[] = scale.ticks.map((v) => ({
          pos: yScale(v),
          label: formatTick(v),
        }));

        const bandW = n > 0 ? innerW / n : innerW;
        const cx = (i: number) => (n > 1 ? plotLeft + (innerW * i) / (n - 1) : plotLeft + innerW / 2);

        const xTicks: AxisTick[] = categories.map((label, i) => ({ pos: cx(i), label }));

        // Break each series into gap-free segments.
        const segmentsFor = (s: LineSeries): string[] => {
          const segs: string[] = [];
          let cur: string[] = [];
          s.values.forEach((v, i) => {
            if (v === null || v === undefined) {
              if (cur.length > 0) {
                segs.push(cur.join(' '));
                cur = [];
              }
            } else {
              cur.push(`${cx(i)},${yScale(v)}`);
            }
          });
          if (cur.length > 0) segs.push(cur.join(' '));
          return segs;
        };

        const lastDefined = (s: LineSeries): number => {
          for (let i = s.values.length - 1; i >= 0; i -= 1) {
            const v = s.values[i];
            if (v !== null && v !== undefined) return i;
          }
          return -1;
        };

        return (
          <>
            <Axis
              left={plotLeft}
              top={plotTop}
              innerWidth={innerW}
              innerHeight={innerH}
              yTicks={yTicks}
              xTicks={xTicks}
              baselineY={showBaseline ? yScale(0) : undefined}
            />

            {series.map((s) => (
              <g key={s.key} opacity={hover === null ? 1 : 0.9}>
                {segmentsFor(s).map((pts, si) => (
                  <polyline
                    key={si}
                    points={pts}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                ))}
                {s.values.map((v, i) =>
                  v === null || v === undefined ? null : (
                    <circle
                      key={`${s.key}-dot-${i}`}
                      cx={cx(i)}
                      cy={yScale(v)}
                      r={hover === i ? 4.5 : 3}
                      fill={s.color}
                    />
                  ),
                )}
                {directLabelLast && lastDefined(s) >= 0
                  ? (() => {
                      const li = lastDefined(s);
                      const v = s.values[li] as number;
                      return (
                        <text
                          x={cx(li)}
                          y={yScale(v) - 8}
                          textAnchor="end"
                          fontSize={11}
                          fontWeight={600}
                          fill={s.color}
                        >
                          {formatValue(v)}
                        </text>
                      );
                    })()
                  : null}
              </g>
            ))}

            <g onPointerLeave={() => setHover(null)}>
              {categories.map((_, i) => (
                <rect
                  key={`hit-${i}`}
                  x={plotLeft + bandW * i}
                  y={plotTop}
                  width={bandW}
                  height={innerH}
                  fill="transparent"
                  onPointerEnter={() => setHover(i)}
                />
              ))}
              {hover !== null ? (
                <line
                  x1={cx(hover)}
                  x2={cx(hover)}
                  y1={plotTop}
                  y2={plotBottom}
                  stroke="var(--viz-axis)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  pointerEvents="none"
                />
              ) : null}
            </g>

            {hover !== null ? (
              <Tooltip
                x={cx(hover)}
                y={plotTop}
                title={categories[hover]}
                chartWidth={width}
                rows={series.map<TooltipRow>((s) => {
                  const v = s.values[hover];
                  return {
                    label: s.label,
                    value: v === null || v === undefined ? '–' : formatValue(v),
                    color: s.color,
                  };
                })}
              />
            ) : null}
          </>
        );
      }}
    </ChartFrame>
  );
}
